import * as PIXI from 'pixi.js';
import { TypedUpdateFlowMixin } from '../../../updateFlowMixin';
import { TypedParentMixin } from '../../../parentMixin';
import { TypedSerializableMixin } from '../../../serializableMixin';
import { BooleanAttribute, NumberAttribute, StringAttribute } from '../../../attributes';
import { DynamicYAlign, DynamicLevel, DynamicXAlign } from '../../../../enums';
import type { DynamicGraphicType } from '../types';
import type { TextDynamicGraphicRendererSettingsBase } from '../renderer/TextDynamicGraphicRenderer';
import type { ParentMixinSettings } from '../../../parentMixin';
import type { Resolution } from '../../../../types';
import { TextAssetDynamicAnimation } from './TextAssetDynamicAnimation';

type TextAssetDynamicGraphicSettings = ParentMixinSettings & {
    referenceToOutputMultiplier: number;
    getReferenceResolution: () => Resolution;
    getOutputResolution: () => Resolution;
};

export abstract class TextAssetDynamicGraphic extends TypedUpdateFlowMixin(
    TypedParentMixin(TypedSerializableMixin(PIXI.utils.EventEmitter)),
) {
    override _classType = 'CanvasTextDynamicGraphic';

    abstract readonly type: DynamicGraphicType;
    @BooleanAttribute()
    accessor enabled = true;
    // TODO: implement asMask behaviour. This is currently not supported. We only want to use a mask as a mask if this
    //  property is set to true. Otherwise we want to use it as a color graphic. This is so the user can see the shape
    //  of the graphic in the editor. Note, we only want to deal with this on the bridge level. Our canvas API does not
    //  need to be aware of this.
    @BooleanAttribute()
    accessor asMask = false;
    @StringAttribute({ choices: Object.values(DynamicLevel) })
    accessor level = DynamicLevel.object;
    @StringAttribute({ choices: Object.values(DynamicXAlign) })
    accessor alignX = DynamicXAlign.left;
    @StringAttribute({ choices: Object.values(DynamicYAlign) })
    accessor alignY = DynamicYAlign.top;
    @NumberAttribute()
    accessor positionX = 0;
    @NumberAttribute()
    accessor positionY = 0;

    #referenceToOutputMultiplier = 1;

    #getReferenceResolution: () => Resolution;
    #getOutputResolution: () => Resolution;

    #animationConfigurations: TextAssetDynamicAnimation[] = [];

    constructor(settings: TextAssetDynamicGraphicSettings) {
        super(settings);

        this.#referenceToOutputMultiplier = settings.referenceToOutputMultiplier;
        this.#getReferenceResolution = settings.getReferenceResolution;
        this.#getOutputResolution = settings.getOutputResolution;
    }

    /**
     * The scaling multiplier to take a reference dimension to the output dimension.
     * e.g. myOutputCoord =  myReferenceCoord * this.referenceToOutputMultiplier
     */
    get referenceToOutputMultiplier() {
        return this.#referenceToOutputMultiplier;
    }

    getCanvasRendererSettings(): TextDynamicGraphicRendererSettingsBase {
        // convert asset animations to canvas animations
        const animationConfigurations = this.#animationConfigurations.map(
            (animationConfiguration) => animationConfiguration.getCanvasRendererSettings(),
        );

        return {
            type: this.type,
            enabled: this.enabled,
            level: this.level,
            alignX: this.alignX,
            alignY: this.alignY,
            positionX: this.positionX * this.referenceToOutputMultiplier,
            positionY: this.positionY * this.referenceToOutputMultiplier,
            animationConfigurations: animationConfigurations,
            asMask: this.asMask,
        };
    }

    get animationConfigurations() {
        return this.#animationConfigurations;
    }

    _animationConfigurationsCustomLoad(properties: Record<string, unknown>[] | undefined) {
        if (!properties) {
            return;
        }
        this.#animationConfigurations.splice(properties.length);
        properties.forEach((data, index) => {
            const animation = this.#animationConfigurations[index] ?? this.addAnimation();
            animation.load(data);
        });
    }

    /**
     * Adds a TextAssetDynamicAnimation to the `animation` list.
     * The added configurator can be configured to animate any attribute on the asset.
     */
    addAnimation() {
        const animation = new TextAssetDynamicAnimation({
            parent: this,
            getReferenceResolution: this.#getReferenceResolution,
            getOutputResolution: this.#getOutputResolution,
        });

        // do not mutate, we need to create a new reference for React
        this.#animationConfigurations = [...this.#animationConfigurations, animation];
        this.requiresUpdate = true;

        return animation;
    }

    removeAnimation(index: number) {
        // do not mutate, we need to create a new reference for React
        const animation = [...this.#animationConfigurations];
        animation.splice(index, 1);
        this.#animationConfigurations = animation;
        this.requiresUpdate = true;
    }

    override get propertiesToSerialize() {
        // why does getAttributesToSerialize(this) not work?
        return [
            'animationConfigurations',
            'enabled',
            'asMask',
            'level',
            'alignX',
            'alignY',
            'positionX',
            'positionY',
        ];
    }

    /** The list of properties that can be animated during draw */
    abstract get animatableProperties(): string[];

    /** The list of geometry properties that can be animated during draw. */
    abstract get animatableGeometricProperties(): string[];

    override get readonlyProperties() {
        return ['type'];
    }
}
